import { Box, Stack, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HorizontalSkeletonCard } from '@omnea-app/fe-legacy/design-system/layout/cards/HorizontalCardSkeleton'
import { InfiniteScrollLoader } from '@omnea-app/fe-tables/mobile/InfiniteScrollLoader'
import { MobileTableSearch } from '@omnea-app/fe-tables/mobile/MobileTableSearch'
import { matchesSupplierStepMobileCardContent, SupplierStepMobileCard } from './SupplierStepMobileCard'
import type { SupplierStep } from '../useGetSupplierSteps'

type MobileSupplierStepsTableProps = {
  steps: SupplierStep[]
  loading: boolean
}

const ITEMS_PER_PAGE = 5
const SKELETON_KEYS = Array.from({ length: ITEMS_PER_PAGE }, (_, i) => i)

export const MobileSupplierStepsTable = ({ steps, loading }: MobileSupplierStepsTableProps) => {
  const { t } = useTranslation('supplier-dashboard', { keyPrefix: 'tasks-table' })
  const [searchValue, setSearchValue] = useState('')
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE)

  const filteredSteps = useMemo(() => {
    if (!searchValue.trim()) return steps

    const searchLower = searchValue.toLowerCase()
    return steps.filter((step) => matchesSupplierStepMobileCardContent(step, searchLower))
  }, [steps, searchValue])

  const handleSearchChange = useCallback((newSearchValue: string) => {
    setSearchValue(newSearchValue)
    setVisibleCount(ITEMS_PER_PAGE)
  }, [])

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, filteredSteps.length))
  }, [filteredSteps.length])

  if (loading) {
    return (
      <Stack spacing={3}>
        {SKELETON_KEYS.map((key) => (
          <HorizontalSkeletonCard key={key} />
        ))}
      </Stack>
    )
  }

  const hasMoreItems = visibleCount < filteredSteps.length

  return (
    <Stack spacing={5}>
      <MobileTableSearch value={searchValue} onChange={handleSearchChange} ariaLabel={t('mobile.search.aria-label')} />

      <Stack spacing={3}>
        {filteredSteps.length === 0 ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography>{t('no-tasks')}</Typography>
          </Box>
        ) : (
          <>
            {filteredSteps.slice(0, visibleCount).map((step) => (
              <SupplierStepMobileCard key={step.id} step={step} />
            ))}
            <InfiniteScrollLoader hasMoreItems={hasMoreItems} onReveal={handleLoadMore} />
          </>
        )}
      </Stack>
    </Stack>
  )
}
